import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

  constructor(
    private toastr: ToastrService,
    private router: Router
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleError(error);
        return throwError(error);
      })
    );
  }

  private handleError(error: HttpErrorResponse): void {
    console.error('HTTP Error Interceptor:', error);
    
    const errorMessage = this.extractErrorMessage(error);
    const shouldLogout = this.shouldLogoutUser(error.status);

    // Show appropriate toast notification
    this.showErrorNotification(error.status, errorMessage);

    // Logout user only for authentication errors
    if (shouldLogout) {
      this.logoutUser();
    }
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    // Priority order for extracting error messages
    if (error.error?.messageDetail) {
      return error.error.messageDetail;
    }
    
    // Handle validation errors array (if backend sends multiple field errors)
    if (error.error?.errors && Array.isArray(error.error.errors)) {
      const errorMessages = error.error.errors.map(err => err.message || err.field).join(', ');
      return `Errores de validación: ${errorMessages}`;
    }
    
    if (error.error?.message) {
      return error.error.message;
    }
    
    if (error.message) {
      return error.message;
    }
    
    if (error.statusText) {
      return error.statusText;
    }

    // Default messages based on status code
    return this.getDefaultErrorMessage(error.status);
  }

  private getDefaultErrorMessage(status: number): string {
    switch (status) {
      case 0:
        return 'No hay conexión con el servidor. Verifique su conexión a internet.';
      case 400:
        return 'Solicitud inválida. Verifique los datos enviados.';
      case 401:
        return 'Su sesión ha expirado. Por favor, inicie sesión nuevamente.';
      case 403:
        return 'No tiene permisos para realizar esta acción.';
      case 404:
        return 'El recurso solicitado no fue encontrado.';
      case 500:
        return 'Error interno del servidor. Intente nuevamente más tarde.';
      case 503:
        return 'Servicio no disponible temporalmente. Intente más tarde.';
      default:
        return 'Ha ocurrido un error inesperado. Contacte con soporte técnico.';
    }
  }

  private shouldLogoutUser(status: number): boolean {
    // Only logout for authentication/authorization errors
    return status === 401 || status === 403;
  }

  private showErrorNotification(status: number, message: string): void {
    const title = this.getErrorTitle(status);
    
    switch (status) {
      case 0:
        this.toastr.error(message, title, { timeOut: 8000 });
        break;
      case 401:
      case 403:
        this.toastr.error(message, title, { timeOut: 5000 });
        break;
      case 400:
      case 404:
        this.toastr.warning(message, title, { timeOut: 6000 });
        break;
      case 500:
      case 503:
        this.toastr.error(message, title, { timeOut: 8000 });
        break;
      default:
        this.toastr.error(message, title, { timeOut: 6000 });
        break;
    }
  }

  private getErrorTitle(status: number): string {
    switch (status) {
      case 0:
        return 'Error de Conexión';
      case 400:
        return 'Solicitud Inválida';
      case 401:
        return 'Sesión Expirada';
      case 403:
        return 'Sin Permisos';
      case 404:
        return 'No Encontrado';
      case 500:
        return 'Error del Servidor';
      case 503:
        return 'Servicio No Disponible';
      default:
        return 'Error';
    }
  }

  private logoutUser(): void {
    // Clear local storage
    localStorage.clear();
    
    // Navigate to login page
    this.router.navigate(['/']);
  }
}

// Export the interceptor provider
export const HttpErrorInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: HttpErrorInterceptor,
  multi: true
};

